import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import Header from "../components/Header";
import Nav from "../components/Nav";
import ServerPagination from "../components/ServerPagination";
import { sortItems, useSortState, SortTh, useColumnVisibility, ColumnMenu } from "../hooks/tableTools";

const PER_PAGE_OPTIONS = [50, 100, 200, 300, 500, 1000];

interface HistColumn {
  key: string;
  label: string;
  sortKey: string;
  render: (r: any) => React.ReactNode;
}

const COLUMNS: HistColumn[] = [
  { key: "data_asset_name", label: "Data Asset", sortKey: "data_asset_name", render: (r) => <span className="asset-cell" title={r.data_asset_name}>{r.data_asset_name}</span> },
  { key: "day_of_week", label: "Day", sortKey: "day_of_week", render: (r) => <span className="dow-pill">{r.day_of_week}</span> },
  { key: "slot_index", label: "Slot #", sortKey: "slot_index", render: (r) => r.slot_index },
  { key: "hist_avg_start", label: "Hist Avg Start (UTC)", sortKey: "hist_avg_start", render: (r) => <span className="time-cell">{r.hist_avg_start || "-"}</span> },
  { key: "hist_avg_end", label: "Hist Avg End (UTC)", sortKey: "hist_avg_end", render: (r) => <span className="time-cell">{r.hist_avg_end || "-"}</span> },
  { key: "hist_avg_duration_mins", label: "Avg Duration", sortKey: "hist_avg_duration_mins", render: (r) => <span className="dur-cell">{r.hist_avg_duration_mins != null ? `${Math.round(r.hist_avg_duration_mins)}m` : "-"}</span> },
  { key: "run_count", label: "Runs (90d)", sortKey: "run_count", render: (r) => <span className="cnt-cell">{r.run_count}</span> },
  { key: "avg_records_in", label: "Avg Rec In", sortKey: "avg_records_in", render: (r) => (r.avg_records_in != null ? Math.round(r.avg_records_in).toLocaleString() : "?") },
  { key: "avg_records_out", label: "Avg Rec Out", sortKey: "avg_records_out", render: (r) => (r.avg_records_out != null ? Math.round(r.avg_records_out).toLocaleString() : "?") },
  { key: "vol_run_count", label: "Vol Runs (14d)", sortKey: "vol_run_count", render: (r) => r.vol_run_count ?? "-" },
];
const COLUMN_KEYS = COLUMNS.map((c) => c.key);

export default function SilverHistory() {
  const [q, setQ] = useState("");
  const [day, setDay] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [computedAt, setComputedAt] = useState<string | null>(null);
  const [perPage, setPerPage] = useState(300);
  const { sortKey, sortDir, onSort, clearSort } = useSortState();
  const { hidden, toggle, selectAll, allChecked } = useColumnVisibility(COLUMN_KEYS);

  async function load() {
    const params: Record<string, any> = { page, per_page: perPage };
    if (q) params.q = q;
    if (day) params.day = day;
    const { data } = await api.histStats(params);
    if (data) {
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setComputedAt(data.computed_at);
    }
  }

  // Day/page/per-page filters apply immediately; only the free-text search is debounced.
  useEffect(() => { load(); }, [page, day, perPage]);
  useEffect(() => {
    const id = setTimeout(() => { setPage(1); load(); }, 300);
    return () => clearTimeout(id);
  }, [q]);

  function clearAllFilters() {
    setQ("");
    setDay("");
    setPage(1);
  }

  const sortedRows = useMemo(() => sortItems(rows, sortKey, sortDir), [rows, sortKey, sortDir]);
  const visibleColumns = COLUMNS.filter((c) => !hidden.has(c.key));
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const computedLabel = computedAt ? computedAt.slice(0, 16).replace("T", " ") + " UTC" : null;
  const hasVol = rows.some((r: any) => r.avg_records_out != null);
  const volComputedAt = hasVol && rows[0] && rows[0].vol_computed_at
    ? rows[0].vol_computed_at.slice(0, 16).replace("T", " ") + " UTC" : null;

  return (
    <>
      <Header
        right={<span id="last-computed-hdr" style={{ color: "#fff", fontSize: ".78rem" }}>{computedAt ? `Computed: ${computedAt}` : "\u2014"}</span>}
      />
      <Nav />

      <div className="controls-bar">
        <input id="q-input" type="text" placeholder="Search asset name…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select id="day-sel" value={day} onChange={(e) => { setDay(e.target.value); setPage(1); }}>
          <option value="">All Days</option>
          <option value="Mon">Monday</option><option value="Tue">Tuesday</option><option value="Wed">Wednesday</option>
          <option value="Thu">Thursday</option><option value="Fri">Friday</option><option value="Sat">Saturday</option><option value="Sun">Sunday</option>
        </select>
        <div className="controls-actions">
          <button type="button" className="btn-sm" onClick={clearSort}>&#10005; Clear Sort</button>
          <button type="button" className="btn-sm" onClick={clearAllFilters}>&#10005; Clear Filters</button>
          <ColumnMenu columns={COLUMNS} hidden={hidden} toggle={toggle} selectAll={selectAll} allChecked={allChecked} />
        </div>
      </div>

      <div className="meta-bar">
        <span id="meta-total">{total} rows</span>
        <span id="meta-computed">{computedAt ? `Last computed: ${computedAt}` : ""}</span>
      </div>

      <div className="hist-table-wrap">
        <table className="hist-table">
          <thead>
            <tr>
              {visibleColumns.map((c) => (
                <SortTh key={c.key} columnKey={c.sortKey} sortKey={sortKey} sortDir={sortDir} onSort={onSort}>{c.label}</SortTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr className="no-data-row"><td colSpan={visibleColumns.length}>
                No historical stats found yet.
              </td></tr>
            ) : sortedRows.map((r, idx) => (
              <tr key={idx}>
                {visibleColumns.map((c) => <td key={c.key}>{c.render(r)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ServerPagination page={page} totalPages={totalPages} total={total} perPage={perPage} perPageOptions={PER_PAGE_OPTIONS}
        onPerPageChange={(n) => { setPerPage(n); setPage(1); }} onPageChange={setPage} />
    </>
  );
}

